
import React, { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Paperclip, Image, FileText, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { v4 as uuidv4 } from 'uuid';

export interface UploadedFile {
  id: string;
  type: "image" | "document";
  file: File;
  previewUrl?: string;
}

interface AttachmentUploaderProps {
  onAttachmentsSelected: (files: UploadedFile[]) => void;
  selectedFiles: UploadedFile[];
}

export const AttachmentUploader: React.FC<AttachmentUploaderProps> = ({ 
  onAttachmentsSelected,
  selectedFiles 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>, type: "image" | "document") => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newFiles: UploadedFile[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Verificar tamanho do arquivo (limite de 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "Arquivo muito grande",
          description: `O arquivo ${file.name} excede o limite de 10MB.`,
          variant: "destructive",
        });
        continue;
      }

      const newFile: UploadedFile = {
        id: uuidv4(),
        type,
        file
      };

      // Criar URL de prévia para imagens
      if (type === "image") {
        newFile.previewUrl = URL.createObjectURL(file);
      }

      newFiles.push(newFile);
    }

    if (newFiles.length > 0) {
      onAttachmentsSelected([...selectedFiles, ...newFiles]);
      setIsOpen(false);
    }

    // Limpar o valor do input para permitir selecionar o mesmo arquivo novamente
    event.target.value = "";
  };

  const removeFile = (id: string) => {
    const updatedFiles = selectedFiles.filter(file => file.id !== id);
    
    // Liberar URLs de prévia para evitar vazamento de memória
    const fileToRemove = selectedFiles.find(file => file.id === id);
    if (fileToRemove?.previewUrl) {
      URL.revokeObjectURL(fileToRemove.previewUrl);
    }
    
    onAttachmentsSelected(updatedFiles);
  };

  return (
    <div className="flex items-center">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon" className="h-9 w-9 rounded-full">
            <Paperclip className="h-5 w-5 text-gray-500" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-2" align="end">
          <div className="space-y-2">
            <Button 
              variant="outline" 
              className="w-full justify-start"
              onClick={() => imageInputRef.current?.click()}
            >
              <Image className="h-4 w-4 mr-2" />
              Imagem
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start"
              onClick={() => documentInputRef.current?.click()}
            >
              <FileText className="h-4 w-4 mr-2" />
              Documento
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <input
        type="file"
        ref={imageInputRef}
        accept="image/*"
        className="hidden"
        multiple
        onChange={(e) => handleFileSelect(e, "image")}
      />
      
      <input
        type="file"
        ref={documentInputRef}
        className="hidden"
        multiple
        onChange={(e) => handleFileSelect(e, "document")}
      />

      {selectedFiles.length > 0 && (
        <div className="ml-2 flex flex-wrap gap-2">
          {selectedFiles.map((file) => (
            <div 
              key={file.id} 
              className="flex items-center bg-gray-100 rounded-full px-2 py-1"
            >
              {file.type === "image" ? (
                <Image className="h-3 w-3 mr-1" />
              ) : (
                <FileText className="h-3 w-3 mr-1" />
              )}
              <span className="text-xs truncate max-w-[100px]">
                {file.file.name}
              </span>
              <button 
                className="ml-1 text-gray-500 hover:text-gray-700"
                onClick={() => removeFile(file.id)}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Função auxiliar para fazer upload de arquivos para o Supabase Storage
export const uploadAttachment = async (file: File, companyId: string): Promise<string | null> => {
  try {
    // Verifica se o bucket existe e o cria se necessário
    const { data: buckets } = await supabase.storage.listBuckets();
    const chatAttachmentsBucket = buckets?.find(b => b.name === 'chat_attachments');
    
    if (!chatAttachmentsBucket) {
      console.warn('O bucket chat_attachments não existe, usando fallback para imagens públicas');
      // Usando um bucket público como fallback
      return URL.createObjectURL(file);
    }
    
    const fileExt = file.name.split('.').pop();
    const fileName = `${uuidv4()}.${fileExt}`;
    const filePath = `${companyId}/${fileName}`;

    const { data, error } = await supabase.storage
      .from('chat_attachments')
      .upload(filePath, file);

    if (error) {
      console.error('Erro ao fazer upload:', error);
      // Fallback para URL local em caso de erro
      return URL.createObjectURL(file);
    }

    const { data: urlData } = supabase.storage
      .from('chat_attachments')
      .getPublicUrl(filePath);

    return urlData.publicUrl;
  } catch (error) {
    console.error('Erro ao fazer upload:', error);
    // Fallback para URL local em caso de erro
    return URL.createObjectURL(file);
  }
};
